// src/pages/RestaurantDashboardPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { QRCodeCanvas } from 'qrcode.react';
import LoadingSpinner from '../components/loadingspinner'; // Assuming this component exists
import { trackPageView, trackAdminAction } from '../services/analyticsService';
import './RestaurantDashboardPage.css'; // We'll create this CSS file later for styling
import QRCodeDownloadTemplate from '../components/QRCodeDownloadTemplate';
import Switch from '../components/Switch'; // Import the Switch component for toggles

function RestaurantDashboardPage() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);
  const [activeMenuName, setActiveMenuName] = useState('No active menu');
  
  // Menu access states
  const [menuEnabled, setMenuEnabled] = useState(true);
  const [tableBookingEnabled, setTableBookingEnabled] = useState(true);
  const [onlineOrderingEnabled, setOnlineOrderingEnabled] = useState(true);
  
  // Restaurant status states
  const [isRestaurantClosed, setIsRestaurantClosed] = useState(false);
  
  // Auto opening/closing time states
  const [openingTime, setOpeningTime] = useState('09:00');
  const [closingTime, setClosingTime] = useState('22:00');
  const [useAutoTimes, setUseAutoTimes] = useState(false);

  const menuUrl = restaurant ? `${window.location.origin}/${restaurant.slug}` : '';

  useEffect(() => {
    trackPageView();
  }, []);

  useEffect(() => {
    if (!restaurantId) {
      setError('Restaurant ID is missing.');
      setLoading(false);
      return;
    }

    const fetchRestaurantData = async () => {
      try {
        setLoading(true);
        const restaurantRef = doc(db, 'restaurants', restaurantId);
        const restaurantSnap = await getDoc(restaurantRef);

        if (restaurantSnap.exists()) {
          const restaurantData = { id: restaurantSnap.id, ...restaurantSnap.data() };
          setRestaurant(restaurantData);
          
          // Initialize states from restaurant data
          setMenuEnabled(restaurantData.menuEnabled !== false); // Default to true if not set
          setTableBookingEnabled(restaurantData.tableBookingEnabled !== false); // Default to true if not set
          setOnlineOrderingEnabled(restaurantData.onlineOrderingEnabled !== false); // Default to true if not set
          setIsRestaurantClosed(restaurantData.isClosed === true); // Default to false if not set
          setOpeningTime(restaurantData.openingTime || '09:00');
          setClosingTime(restaurantData.closingTime || '22:00');
          setUseAutoTimes(restaurantData.useAutoTimes === true); // Default to false if not set
          
          // Fetch active menu name
          try {
            const menusRef = collection(db, 'restaurants', restaurantId, 'menus');
            const activeMenuQuery = query(menusRef, where('isActive', '==', true), orderBy('createdAt', 'desc'));
            const activeMenuSnap = await getDocs(activeMenuQuery);
            
            if (!activeMenuSnap.empty) {
              const activeMenu = activeMenuSnap.docs[0];
              setActiveMenuName(activeMenu.data().name);
            } else {
              setActiveMenuName('Legacy Menu (Default)');
            }
          } catch (menuError) {
            console.error('Error fetching active menu:', menuError);
            setActiveMenuName('Legacy Menu (Default)');
          }
          
          trackAdminAction('view_dashboard', { restaurantId: restaurantSnap.id, restaurantName: restaurantSnap.data().name });
        } else {
          setError('Restaurant not found.');
          navigate('/404'); // Or a more appropriate error page
        }
      } catch (err) {
        console.error('Error fetching restaurant data:', err);
        setError('Failed to load restaurant data.');
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurantData();
  }, [restaurantId, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="container error-message">{error}</div>;
  }

  if (!restaurant) {
    return <div className="container">Restaurant data could not be loaded.</div>;
  }

  const handleNavigate = (path) => {
    trackAdminAction(`dashboard_navigate_${path.split('/').pop()}`, { restaurantId });
    navigate(path);
  };
  
  // Function to update restaurant settings in Firestore
  const updateRestaurantSetting = async (field, value) => {
    try {
      const restaurantRef = doc(db, 'restaurants', restaurantId);
      await updateDoc(restaurantRef, { [field]: value });
      console.log(`Updated ${field} to ${value}`);
      
      // Update local state to reflect the change
      setRestaurant(prev => ({ ...prev, [field]: value }));
      
      // Track the action
      trackAdminAction(`update_restaurant_setting_${field}`, { restaurantId, value });
      
      return true;
    } catch (err) {
      console.error(`Error updating ${field}:`, err);
      setError(`Failed to update ${field}. Please try again.`);
      return false;
    }
  };
  
  // Toggle handlers for menu access
  const handleMenuToggle = async () => {
    const newValue = !menuEnabled;
    const success = await updateRestaurantSetting('menuEnabled', newValue);
    if (success) setMenuEnabled(newValue);
  };
  
  const handleTableBookingToggle = async () => {
    const newValue = !tableBookingEnabled;
    const success = await updateRestaurantSetting('tableBookingEnabled', newValue);
    if (success) setTableBookingEnabled(newValue);
  };
  
  const handleOnlineOrderingToggle = async () => {
    const newValue = !onlineOrderingEnabled;
    const success = await updateRestaurantSetting('onlineOrderingEnabled', newValue);
    if (success) setOnlineOrderingEnabled(newValue);
  };
  
  // Toggle handler for restaurant closed status
  const handleRestaurantClosedToggle = async () => {
    const newValue = !isRestaurantClosed;
    const success = await updateRestaurantSetting('isClosed', newValue);
    if (success) setIsRestaurantClosed(newValue);
  };
  
  // Handlers for auto opening/closing times
  const handleOpeningTimeChange = async (e) => {
    const newValue = e.target.value;
    const success = await updateRestaurantSetting('openingTime', newValue);
    if (success) setOpeningTime(newValue);
  };
  
  const handleClosingTimeChange = async (e) => {
    const newValue = e.target.value;
    const success = await updateRestaurantSetting('closingTime', newValue);
    if (success) setClosingTime(newValue);
  };
  
  const handleAutoTimesToggle = async () => {
    const newValue = !useAutoTimes;
    const success = await updateRestaurantSetting('useAutoTimes', newValue);
    if (success) setUseAutoTimes(newValue);
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>{restaurant.name} - Dashboard</h1>
      </header>

      <div className="dashboard-grid">
        {/* View Orders - Positioned first as it's very important */}
        <div className="dashboard-card order-card">
          <h2>View Orders</h2>
          <p>Check and manage incoming customer orders.</p>
          <button onClick={() => handleNavigate(`/orders/${restaurantId}`)} className="btn btn-primary">Manage Orders</button>
        </div>
        

        {/* Restaurant Status - Critical for operation */}
        <div className="dashboard-card status-card">
          <h2>Restaurant Status</h2>
          <div className="restaurant-status-control">
            <div className="restaurant-status-label">
              <span>Restaurant is {isRestaurantClosed ? 'Closed' : 'Open'}</span>
              <Switch isOn={!isRestaurantClosed} handleToggle={handleRestaurantClosedToggle} />
            </div>
            <p className="restaurant-status-message">
              {isRestaurantClosed 
                ? 'Customers will see "Restaurant is closed" message' 
                : 'Restaurant is shown as open to customers'}
            </p>
          </div>
          
          <div className="auto-times-section">
            <h3>Auto Opening/Closing Times</h3>
            <div className="auto-times-toggle">
              <span>Use automatic times</span>
              <Switch isOn={useAutoTimes} handleToggle={handleAutoTimesToggle} />
            </div>
            
            <div className="time-inputs">
              <div className="time-input-group">
                <label htmlFor="opening-time">Opening Time:</label>
                <input 
                  type="time" 
                  id="opening-time" 
                  value={openingTime} 
                  onChange={handleOpeningTimeChange}
                  disabled={!useAutoTimes}
                />
              </div>
              
              <div className="time-input-group">
                <label htmlFor="closing-time">Closing Time:</label>
                <input 
                  type="time" 
                  id="closing-time" 
                  value={closingTime} 
                  onChange={handleClosingTimeChange}
                  disabled={!useAutoTimes}
                />
              </div>
            </div>
            <p className="time-info">
              {useAutoTimes 
                ? `Restaurant will automatically show as open from ${openingTime} to ${closingTime}` 
                : 'Automatic opening/closing times are disabled'}
            </p>
          </div>
        </div>
        
        {/* Regular Menu Card */}
         <div className="dashboard-card menu-card">
           <h2>Regular Menu</h2>
           <p>Control access to your restaurant's regular menu.</p>
           
           <div className="menu-access-control">
             <div className="menu-access-label">
               <span>Menu Status</span>
               <Switch isOn={menuEnabled} handleToggle={handleMenuToggle} />
             </div>
             <h3 className="menu-access-status">{menuEnabled ? 'Enabled' : 'Disabled'}</h3>
           </div>
           
           <div className="share-options">
             <h3>Share Menu</h3>
             <div className="url-display">
               <input type="text" value={menuUrl} readOnly className="share-url" />
               <button 
                 onClick={() => {navigator.clipboard.writeText(menuUrl); alert('Menu URL copied to clipboard!');}} 
                 className="btn btn-sm btn-secondary"
                 title="Copy URL"
               >
                 Copy
               </button>
             </div>
             <div className="share-buttons">
               <button onClick={() => { trackAdminAction('visit_live_menu_from_dashboard', { restaurantId }); window.open(menuUrl, '_blank');}} className="btn btn-primary">View Menu</button>
               <button 
                 onClick={() => window.open(`https://wa.me/?text=Check out our menu: ${encodeURIComponent(menuUrl)}`, '_blank')} 
                 className="btn btn-outline"
               >
                 Share
               </button>
             </div>
           </div>
         </div>

         {/* Table Booking Card */}
         <div className="dashboard-card booking-card">
           <h2>Table Booking</h2>
           <p>Control access to your restaurant's table booking feature.</p>
           
           <div className="menu-access-control">
             <div className="menu-access-label">
               <span>Booking Status</span>
               <Switch isOn={tableBookingEnabled} handleToggle={handleTableBookingToggle} />
             </div>
             <h3 className="menu-access-status">{tableBookingEnabled ? 'Enabled' : 'Disabled'}</h3>
           </div>
           
           <div className="share-options">
             <h3>Book Table</h3>
             {/* <div className="url-display">
               <input type="text" value={`${menuUrl}/table-booking`} readOnly className="share-url" />
               <button 
                 className="btn btn-secondary copy-btn"
                 onClick={() => {navigator.clipboard.writeText(`${menuUrl}/table-booking`); alert('Booking URL copied to clipboard!');}} 
               >
                 Copy Link
               </button>
             </div> */}
             <div className="share-actions">
               <h3>Quick Access</h3>
               <p>Use these buttons to quickly access or share the booking page.</p>
               <button onClick={() => { trackAdminAction('visit_booking_from_dashboard', { restaurantId }); window.open(`${menuUrl}/table-booking`, '_blank');}} className="btn btn-primary"> Table Ordering</button>
               {/* <button 
                 onClick={() => window.open(`https://wa.me/?text=Book a table at our restaurant: ${encodeURIComponent(`${menuUrl}/table-booking`)}`, '_blank')} 
                 className="btn btn-outline"
               >Share
               </button> */}
             </div>
           </div>
         </div>
         
         {/* Online Ordering Card */}
         <div className="dashboard-card ordering-card">
           <h2>Online Ordering</h2>
           <p>Control access to your restaurant's online ordering system.</p>
           
           <div className="menu-access-control">
             <div className="menu-access-label">
               <span>Ordering Status</span>
               <Switch isOn={onlineOrderingEnabled} handleToggle={handleOnlineOrderingToggle} />
             </div>
             <h3 className="menu-access-status">{onlineOrderingEnabled ? 'Enabled' : 'Disabled'}</h3>
           </div>
           
           <div className="share-options">
             <h3>Share Ordering Link</h3>
             <div className="url-display">
               <input type="text" value={`${menuUrl}/online-order`} readOnly className="share-url" />
               <button 
                 className="btn btn-secondary copy-btn"
                 onClick={() => {navigator.clipboard.writeText(`${menuUrl}/online-order`); alert('Ordering URL copied to clipboard!');}} 
               >
                 Copy Link
               </button>
             </div>
             <div className="share-actions">
               <h3>Quick Access</h3>
               <p>Use these buttons to quickly access or share the ordering page.</p>
               <button onClick={() => { trackAdminAction('visit_ordering_from_dashboard', { restaurantId }); window.open(`${menuUrl}/online-order`, '_blank');}} className="btn btn-primary">Online Ordering</button>
               <button 
                 onClick={() => window.open(`https://wa.me/?text=Order online from our restaurant: ${encodeURIComponent(`${menuUrl}/online-order`)}`, '_blank')} 
                 className="btn btn-outline"
               >
                 Share
               </button>
             </div>
           </div>
         </div>

        {/* Manage Menus - Multiple menu management */}
        <div className="dashboard-card manage-menus-card">
          <h2>Manage Menus</h2>
          <p>Create and manage multiple menus. Control which menu is active and edit menu items.</p>
          <div className="current-active-menu" style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #e9ecef'
          }}>
            <strong>Current Active Menu:</strong>
            <div style={{
              marginTop: '0.25rem',
              color: '#007bff',
              fontWeight: '500'
            }}>
              {activeMenuName}
            </div>
          </div>
          <button onClick={() => handleNavigate(`/manage-menus/${restaurantId}`)} className="btn btn-primary">Manage Menus</button>
        </div>


        
        {/* Restaurant Settings - Less frequently used */}
        <div className="dashboard-card settings-card">
          <h2>Restaurant Settings</h2>
          <p>Update restaurant details like name, logo, and currency.</p>
          <button onClick={() => handleNavigate(`/restaurant-edit/${restaurantId}`)} className="btn btn-primary">Edit Restaurant Details</button>
        </div>

        {/* QR Code - Utility feature */}
        <div className="dashboard-card qr-code-card">
          <h2>QR Code</h2>
          <p>Share this QR code for customers to access your menu.</p>
          {menuUrl && (
            <div className="qr-code-wrapper">
              <QRCodeCanvas value={menuUrl} size={180} includeMargin={true} />
            </div>
          )}
          <button onClick={() => handleNavigate(`/qr-generator/${restaurantId}`)} className="btn btn-primary mt-2">Generate QR Code</button>
        </div>

        {/* Order Settings - Workflow configuration */}
        <div className="dashboard-card order-settings-card">
          <h2>Order Settings</h2>
          <p>Configure your order workflow and manage order processing stages.</p>
          <button onClick={() => handleNavigate(`/order-settings/${restaurantId}`)} className="btn btn-primary">Configure Order Workflow</button>
        </div>

      </div>

      {showQRModal && menuUrl && (
        <div className="qr-modal-overlay">
          <div className="qr-modal-content">
            <button className="qr-modal-close" onClick={() => setShowQRModal(false)}>×</button>
            <QRCodeDownloadTemplate menuUrl={menuUrl} restaurantName={restaurant.name} />
          </div>
        </div>
      )}
    </div>
  );
}

export default RestaurantDashboardPage;